<template>
    <div class="OrganizationsLists">
        <AppTable
            ref="table"
            :columns="cols"
            :data="normalizeRows"
            :total="total"
            :loading="loading"
            hideHeader
            :rowsPerPage="pagination.rowsPerPage"
            :page="pagination.page"
            rowKey="uuid"
            class="Table"
            @table-request="handleFetchData"
        >
            <template #content="{ row }">
                <q-card
                    bordered
                    class="OrgCard"
                    :class="{ 'cursor-pointer': !row.deleteDate }"
                    @click="handleGoToOrganization(row)"
                >
                    <q-card-section class="OrgCardHeader bg-primary text-white">
                        <div class="flex row-stretch-center no-wrap">
                            <q-icon
                                name="workspaces"
                                size="22px"
                                color="white"
                                class="q-mr-sm"
                            />
                            <div class="OrgCardTitle ellipsis">
                                {{ row.name }}
                            </div>
                            <q-btn
                                icon="more_vert"
                                color="white"
                                dense
                                flat
                                @click.stop="openMenu(row, $event)"
                            />
                        </div>
                    </q-card-section>

                    <q-card-section class="OrgCardBody bg-white">
                        <div class="OrgCardRow">
                            <div class="text-caption">
                                {{ $t('general.createdAt') }}
                            </div>
                            <div class="text-body2">
                                {{ App.dateLocationFormat(row.createDate, 'long') }}
                            </div>
                        </div>

                        <div class="OrgCardRow">
                            <div class="text-caption">
                                {{ $t('general.projectCount') }}
                            </div>
                            <div class="text-body2">
                                {{ row.projectCount }}
                            </div>
                        </div>
                    </q-card-section>
                </q-card>
            </template>

            <template #empty>
                <EmptyList
                    v-bind="emptyMessage"
                    class="mt-2 mb-2"
                >
                    <template #footer>
                        <QBtn
                            class="mt-1"
                            color="primary"
                            noCaps
                            :label="$t('organizations.new')"
                            icon="add_circle_outline"
                            @click="$emit('onNewItem')"
                        />
                    </template>
                </EmptyList>
            </template>
        </AppTable>

        <q-menu
            v-if="menuActivator"
            v-model="menuOpen"
            :target="menuActivator"
            @hide="cleanSelected"
        >
            <q-list dense class="text-caption">
                <q-item
                    v-for="option in menuOptions"
                    :key="option.key"
                    clickable
                    @click="handleClickOption(option)"
                >
                    <q-item-section avatar>
                        <q-icon :name="option.icon" :color="option.iconColor" />
                    </q-item-section>
                    <q-item-section>
                        {{ option.label }}
                    </q-item-section>
                </q-item>
            </q-list>
        </q-menu>
    </div>
</template>

<script>
import VueTypes from 'vue-types'
import { Table as AppTable, EmptyList } from '@/components'
import { filtersMixin } from '@/mixins'

const ACTIONS = {
    DELETE: 'DELETE',
    EDIT: 'EDIT',
}

export default {
    name: 'OrganizationsLists',
    components: {
        AppTable,
        EmptyList,
    },
    mixins: [filtersMixin],
    inject: ['App'],
    props: {
        columns: VueTypes.array,
        rows: VueTypes.array,
        total: VueTypes.number,
        search: VueTypes.string,
        loading: VueTypes.bool.def(false),
        pagination: VueTypes.shape({
            page: VueTypes.number,
            rowsPerPage: VueTypes.number,
            rowsNumber: VueTypes.number,
            sortBy: VueTypes.string,
            sortDirection: VueTypes.string,
        }).loose.def({
            page: 1,
            rowsPerPage: 10,
        }),
    },
    emits: ['onNewItem', 'onClick', 'onFetchData', 'onUpdate', 'onDelete'],
    data() {
        return {
            selected: undefined,
            menuOpen: false,
            menuActivator: null,
            menuOptions: [],
            sortableColumns: ['NAME', 'CREATED_AT'],
            sortBy: localStorage.getItem('organizationsListSortBy'),
            direction: localStorage.getItem('organizationsListSortDirection'),
        }
    },
    computed: {
        cols() {
            return [
                ...this.columns,
                {
                    name: 'NAME',
                    label: this.$t('general.organization'),
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'CREATED_AT',
                    label: this.$t('general.createdAt'),
                    align: 'left',
                    sortable: true,
                },
                {
                    name: 'projectCount',
                    label: this.$t('general.projectCount'),
                    align: 'left',
                    sortable: false,
                },
                {
                    name: 'actions',
                    label: '',
                },
            ]
        },
        hideHeader() {
            return !(this.rows || []).length
        },
        emptyMessage() {
            return {
                title: this.$t(`organizations.notifications.${this.search ? 'noResult.title' : 'empty.title'}`),
                text: this.$t(`organizations.notifications.${this.search ? 'noResult.text' : 'empty.text'}`),
            }
        },
        normalizeRows() {
            return (this.rows || []).map((row) => ({
                ...row,
                options: this.generateOptionsRow(),
            }))
        },
    },
    watch: {
        rows() {
            if (this.menuOpen) this.cleanSelected()
        },
    },
    methods: {
        generateOptionsRow() {
            return [
                {
                    key: ACTIONS.EDIT,
                    icon: 'edit',
                    iconColor: 'primary',
                    label: this.$t('actions.edit'),
                    event: 'onUpdate',
                },
                {
                    key: ACTIONS.DELETE,
                    icon: 'delete',
                    iconColor: 'primary',
                    label: this.$t('actions.delete'),
                    event: 'onDelete',
                },
            ]
        },
        openMenu(row, event) {
            this.selected = row
            this.menuActivator = event?.currentTarget || event?.target || null
            this.menuOptions = row?.options || this.generateOptionsRow()

            if (!this.menuActivator) return
            this.menuOpen = true
        },
        cleanSelected() {
            this.menuOpen = false
            this.selected = undefined
            this.menuActivator = null
            this.menuOptions = []
        },
        isSortable(colName) {
            return this.sortableColumns.includes(colName)
        },
        handleClickOption({ event }) {
            if (event) this.$emit(event, this.selected)
            this.cleanSelected()
        },
        handleGoToOrganization(org) {
            if (!org.deleteDate) this.$emit('onClick', org)
        },
        updateValues(col) {
            const newDirection = this.sortBy === col.name && this.direction === 'ASCENDING'
                ? 'DESCENDING'
                : 'ASCENDING'

            this.sortBy = col.name
            this.direction = newDirection
            localStorage.setItem('organizationsListSortBy', this.sortBy)
            localStorage.setItem('organizationsListSortDirection', this.direction)

            this.$emit('onFetchData', {
                ...this.pagination,
                sortBy: this.sortBy,
                sortDirection: this.direction,
            })
        },
        handleFetchData(props) {
            this.$emit('onFetchData', {
                ...this.pagination,
                page: props.page,
                rowsPerPage: props.rowsPerPage,
                sortBy: this.sortBy,
                sortDirection: this.direction,
            })
        },
    },
}
</script>
<style scoped lang="scss">
.Table:deep() {
    border: none !important;

    .q-table__middle {
        overflow: visible;
    }

    .q-table__bottom {
        margin-top: 20px;
        border: 1px solid $table-border-color;
    }

    tbody {
        display: grid;
        grid-template-columns: repeat(1, minmax(0, 1fr));
        grid-gap: 16px;
        padding: 2px 2px 10px;
        overflow: visible;

        @media screen and (min-width: $md) {
            grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        @media screen and (min-width: $xl) {
            grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        @media screen and (min-width: $xxxl) {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }
    }
}

.OrgCard {
    width: 100%;
    height: 100%;
}

.OrgCardHeader {
    padding: 12px 16px;
}

.OrgCardTitle {
    font-weight: 500;
}

.ellipsis {
    display: inline-block;
    vertical-align: middle;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.OrgCardBody {
    padding: 12px 16px;
}

.OrgCardRow {
    & + & {
        margin-top: 8px;
    }
}
</style>
